import { AvailableLocale } from '@omnea-digital/toolbox'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { throwIfApiError } from 'utils/error-handling/throwifApiError'
import { ConfigHttpClient } from '@omnea-app/config/configHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import {
  ConfigTranslationFunction,
  noOpConfigTranslationFunction,
} from '@omnea-app/packages/universal/i18n/configTranslations/configTranslationFunction'
import { ConfigTranslationEntities } from '@omnea-app/packages/universal/i18n/configTranslations/keys'
import { createI18nInstance } from './getFrontendI18n'
import { useTranslationsContext } from './TranslationsContext'
import type { i18n } from 'i18next'

const ConfigTranslationsContext = createContext<i18n | undefined>(undefined)

const configTranslationsQueryKey = (locale: AvailableLocale) => ['config-translations', locale]

export const useInvalidateConfigTranslations = () => {
  const queryClient = useQueryClient()
  return (locale: AvailableLocale) => queryClient.invalidateQueries({ queryKey: configTranslationsQueryKey(locale) })
}

export const ConfigTranslationsProvider = ({
  children,
  locale: localeProp,
}: {
  children: ReactNode
  locale?: AvailableLocale
}) => {
  const currentLocale = useTranslationsContext().locale
  const locale = localeProp ?? currentLocale
  const { CONFIG_TRANSLATIONS } = useFeatureFlags()

  const { getToken } = useAuthenticatedUser()

  const { data: translations } = useQuery({
    queryKey: configTranslationsQueryKey(locale),
    queryFn: async () => {
      const configClient = new ConfigHttpClient({ authorization: await getToken() })
      const { data, error } = await configClient.GET('/translations', { params: { query: { locale } } })
      throwIfApiError(error)

      return data
    },
    enabled: CONFIG_TRANSLATIONS,
  })

  const i18nInstance = useMemo(
    () => (CONFIG_TRANSLATIONS && translations ? createI18nInstance(locale, { [locale]: translations.translations }) : undefined),
    [locale, CONFIG_TRANSLATIONS, translations]
  )
  // Don't even need the provider if we don't want to render
  if (!CONFIG_TRANSLATIONS) {
    return <>{children}</>
  }

  return <ConfigTranslationsContext.Provider value={i18nInstance}>{children}</ConfigTranslationsContext.Provider>
}

export const useConfigTranslations = (): ConfigTranslationFunction<ConfigTranslationEntities> => {
  const i18nInstance = useContext(ConfigTranslationsContext)
  const { t } = useTranslation(undefined, { i18n: i18nInstance })

  const configTranslation = useCallback(
    ((translationKey, defaultValue) => {
      if (defaultValue === undefined) return undefined

      const { key, entityId } = translationKey

      if (entityId == null) return defaultValue

      return t(`${entityId}.${key}`, { ns: translationKey.namespace, defaultValue })
      // Cast required to satisfy overloads
    }) as ConfigTranslationFunction<ConfigTranslationEntities>,
    [t]
  )

  if (!i18nInstance) {
    return noOpConfigTranslationFunction
  }
  return configTranslation
}
